import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { API_ENDPOINTS } from '../config/api';
import { DUMMY_RESTAURANTS } from '../data/dummyRestaurants';

const RestaurantsPage = () => {
    const [restaurants, setRestaurants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCity, setSelectedCity] = useState('all');
    const [page, setPage] = useState(1);
    const perPage = 20;

    const restaurantFallbackLogo = (r, index) => {
        const label = String(r?.name || r?.city || r?._id || `R${index + 1}`)
            .trim()
            .split(/\s+/)
            .slice(0, 2)
            .map((p) => p[0])
            .join('')
            .slice(0, 4)
            .toUpperCase();
        const bg = '#111827';
        const fg = '#f9fafb';
        const svg = `
          <svg xmlns="http://www.w3.org/2000/svg" width="512" height="512" viewBox="0 0 512 512">
            <defs>
              <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stop-color="${bg}" stop-opacity="1"/>
                <stop offset="1" stop-color="#000000" stop-opacity="0.22"/>
              </linearGradient>
            </defs>
            <rect width="512" height="512" rx="128" fill="url(#g)"/>
            <circle cx="256" cy="256" r="170" fill="rgba(255,255,255,0.08)"/>
            <text x="50%" y="54%" dominant-baseline="middle" text-anchor="middle"
              font-family="ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial"
              font-size="140" font-weight="800" fill="${fg}" letter-spacing="6">${label || 'FOOD'}</text>
          </svg>
        `.trim();
        return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
    };

    const mergeWithDummies = (list) => {
        const combined = [...(Array.isArray(list) ? list : []), ...DUMMY_RESTAURANTS];
        const seen = new Set();
        return combined.filter((r) => {
            const id = r?._id;
            if (!id) return false;
            if (seen.has(id)) return false;
            seen.add(id);
            return true;
        });
    };

    const cities = useMemo(() => {
        const set = new Set();
        restaurants.forEach((r) => {
            const c = (r?.city || '').toString().trim();
            if (c) set.add(c);
        });
        return Array.from(set).sort((a, b) => a.localeCompare(b));
    }, [restaurants]);

    const filteredRestaurants = useMemo(() => {
        const byCity =
            selectedCity === 'all'
                ? restaurants
                : restaurants.filter((r) => (r.city || '').toString().toLowerCase().trim() === selectedCity);

        if (!searchQuery.trim()) return byCity;
        const q = searchQuery.toLowerCase().trim();
        return byCity.filter((r) => {
            const name = (r.name || '').toLowerCase();
            const city = (r.city || '').toLowerCase();
            const state = (r.state || '').toLowerCase();
            const cuisine = (r.cuisine || '').toLowerCase();
            const address = (r.address || '').toLowerCase();
            return name.includes(q) || city.includes(q) || state.includes(q) || cuisine.includes(q) || address.includes(q);
        });
    }, [restaurants, searchQuery, selectedCity]);

    useEffect(() => {
        setPage(1);
    }, [searchQuery, selectedCity]);

    const totalResults = filteredRestaurants.length;
    const totalPages = Math.max(1, Math.ceil(totalResults / perPage));

    useEffect(() => {
        setPage((p) => Math.min(Math.max(1, p), totalPages));
    }, [totalPages]);

    const pagedRestaurants = useMemo(() => {
        const start = (page - 1) * perPage;
        return filteredRestaurants.slice(start, start + perPage);
    }, [filteredRestaurants, page]);

    const showingFrom = totalResults === 0 ? 0 : (page - 1) * perPage + 1;
    const showingTo = Math.min(page * perPage, totalResults);

    const pageButtons = useMemo(() => {
        // compact pagination: first, last, current +-1
        const set = new Set([1, totalPages, page - 1, page, page + 1]);
        const nums = Array.from(set)
            .filter((n) => Number.isFinite(n) && n >= 1 && n <= totalPages)
            .sort((a, b) => a - b);
        const out = [];
        for (let i = 0; i < nums.length; i += 1) {
            const n = nums[i];
            if (i > 0 && n - nums[i - 1] > 1) out.push('…');
            out.push(n);
        }
        return out;
    }, [page, totalPages]);

    const PaginationControls = useMemo(() => {
        if (totalPages <= 1) return null;
        return (
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="text-xs sm:text-sm text-stone-500">
                    Page <span className="font-semibold text-stone-700">{page}</span> of{' '}
                    <span className="font-semibold text-stone-700">{totalPages}</span>
                </div>
                <div className="flex items-center gap-1.5">
                    <button
                        type="button"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page <= 1}
                        className="px-3 py-1.5 rounded-xl border border-stone-200 bg-white text-stone-700 text-xs sm:text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-stone-50"
                    >
                        Prev
                    </button>

                    <div className="flex items-center gap-1">
                        {pageButtons.map((p, idx) =>
                            p === '…' ? (
                                <span key={`ellipsis-${idx}`} className="px-2 text-stone-400 text-xs sm:text-sm">
                                    …
                                </span>
                            ) : (
                                <button
                                    key={p}
                                    type="button"
                                    onClick={() => setPage(p)}
                                    className={[
                                        'min-w-[2.25rem] px-2.5 py-1.5 rounded-xl text-xs sm:text-sm font-semibold border transition-colors',
                                        p === page
                                            ? 'bg-amber-50 border-amber-200 text-amber-800'
                                            : 'bg-white border-stone-200 text-stone-700 hover:bg-stone-50',
                                    ].join(' ')}
                                >
                                    {p}
                                </button>
                            )
                        )}
                    </div>

                    <button
                        type="button"
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page >= totalPages}
                        className="px-3 py-1.5 rounded-xl border border-stone-200 bg-white text-stone-700 text-xs sm:text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-stone-50"
                    >
                        Next
                    </button>
                </div>
            </div>
        );
    }, [page, pageButtons, totalPages]);

    useEffect(() => {
        setLoading(true);
        axios
            .get(API_ENDPOINTS.RESTAURANTS_ALL)
            .then((res) => {
                const list = Array.isArray(res.data) ? res.data : [];
                const active = list.filter((r) => r.isActive !== false && r.active !== false);
                setRestaurants(mergeWithDummies(active));
            })
            .catch(() => setRestaurants(mergeWithDummies([])))
            .finally(() => setLoading(false));
    }, []);

    const formatTime = (t) => {
        if (!t || typeof t !== 'string') return '—';
        const s = t.trim();
        if (!s) return '—';
        return s;
    };

    const container = {
        hidden: { opacity: 0 },
        show: {
            opacity: 1,
            transition: { staggerChildren: 0.06 },
        },
    };

    const item = {
        hidden: { opacity: 0, y: 20 },
        show: { opacity: 1, y: 0 },
    };

    return (
        <div className="min-h-screen bg-stone-50 text-stone-900 overflow-hidden">
            {/* Background atmosphere */}
            <div className="fixed inset-0 pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-b from-amber-100/40 via-transparent to-transparent" />
                <div className="absolute top-0 right-0 w-[80vmax] h-[80vmax] bg-amber-200/30 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-0 left-0 w-[60vmax] h-[60vmax] bg-amber-100/40 rounded-full blur-[100px] translate-y-1/2 -translate-x-1/2" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(251,191,36,0.12),transparent)]" />
            </div>

            {/* Header */}
            <header className="relative z-50 border-b border-stone-200/80 bg-white/70 backdrop-blur-sm">
                <div className="max-w-6xl mx-auto px-3 sm:px-6 py-2.5 sm:py-4">
                    <div className="flex items-center justify-between">
                        <Link
                            to="/"
                            className="flex items-center gap-2 sm:gap-3 text-stone-700 hover:text-stone-900 transition-colors group min-w-0"
                        >
                            <img src="/logo-bright.svg" alt="RestroAI" className="h-7 sm:h-9 w-auto max-w-[90px] sm:max-w-none" />
                            <span className="text-xs sm:text-sm font-medium text-stone-500 group-hover:text-stone-700 hidden sm:inline">Restaurants</span>
                        </Link>
                        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                            <Link
                                to="/pocketbillingai"
                                className="inline-flex items-center justify-center text-xs sm:text-sm font-medium text-indigo-700 hover:text-indigo-900 px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-full bg-indigo-50 border border-indigo-200 hover:border-indigo-300 transition-all duration-300 min-h-[2rem] sm:min-h-[2.5rem] whitespace-nowrap"
                            >
                                PocketBillingAI
                            </Link>
                            <Link
                                to="/login"
                                className="inline-flex items-center justify-center text-xs sm:text-sm font-semibold text-amber-600 hover:text-amber-700 px-3 py-1.5 sm:px-5 sm:py-2.5 rounded-full bg-amber-50 border border-amber-200 hover:border-amber-300 hover:bg-amber-100/80 transition-all duration-300 min-h-[2rem] sm:min-h-[2.5rem]"
                            >
                                Login
                            </Link>
                        </div>
                    </div>
                </div>
            </header>

            <main className="relative z-10 max-w-6xl mx-auto px-3 sm:px-6 pt-6 sm:pt-8 md:pt-10 pb-10 sm:pb-16">
                {/* Hero + Search */}
                <section className="text-center mb-8 sm:mb-12 md:mb-16">
                    <motion.p
                        className="text-amber-600 text-xs sm:text-sm font-semibold tracking-widest uppercase mb-2 sm:mb-4"
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        Discover
                    </motion.p>
                    <motion.h1
                        className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-stone-900 mb-2 sm:mb-4 max-w-2xl mx-auto leading-[1.15]"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.05 }}
                    >
                        Find your next{' '}
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-500 to-amber-600">
                            favourite Restaurant
                        </span>
                    </motion.h1>
                    <motion.p
                        className="text-stone-500 text-sm sm:text-base md:text-lg max-w-md mx-auto mb-6 sm:mb-8 md:mb-10 px-1"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                    >
                        Search by name, city or cuisine. Tap a restaurant to view the menu.
                    </motion.p>

                    <motion.div
                        className="w-full max-w-xl mx-auto relative px-0"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.15 }}
                    >
                        <div className="relative">
                            {/* Unified command bar — compact on mobile; 16px text on inputs avoids iOS zoom */}
                            <div className="flex flex-col sm:flex-row items-stretch rounded-2xl sm:rounded-2xl bg-white/95 sm:bg-white/90 backdrop-blur-md border border-stone-200/90 shadow-md shadow-stone-200/30 sm:shadow-xl sm:shadow-stone-200/50 overflow-hidden focus-within:border-amber-400 focus-within:ring-2 sm:focus-within:ring-4 focus-within:ring-amber-400/15 transition-all duration-300">
                                <div className="flex items-center gap-2 min-h-[2.5rem] sm:min-h-0 px-3 sm:px-4 py-2 sm:py-3 border-b border-stone-200/80 sm:border-b-0 sm:border-r sm:shrink-0 sm:w-[min(100%,13.5rem)]">
                                    <span className="text-base sm:text-base flex-shrink-0 leading-none" aria-hidden>
                                        📍
                                    </span>
                                    <div className="relative flex-1 min-w-0">
                                        <label className="sr-only" htmlFor="restaurants-city-filter">
                                            City
                                        </label>
                                        <select
                                            id="restaurants-city-filter"
                                            value={selectedCity}
                                            onChange={(e) => setSelectedCity(e.target.value)}
                                            className="w-full min-h-0 py-0.5 bg-transparent text-stone-900 focus:outline-none focus:ring-0 text-base font-semibold appearance-none cursor-pointer pr-8 sm:pr-7 rounded-md sm:rounded-none leading-tight"
                                        >
                                            <option value="all">All cities</option>
                                            {cities.map((c) => (
                                                <option key={c} value={c.toLowerCase()}>
                                                    {c}
                                                </option>
                                            ))}
                                        </select>
                                        <span
                                            className="pointer-events-none absolute right-0.5 top-1/2 -translate-y-1/2 text-stone-400 text-[10px] sm:text-[10px] leading-none"
                                            aria-hidden
                                        >
                                            ▼
                                        </span>
                                    </div>
                                </div>

                                <div className="relative flex-1 flex items-center min-h-[2.5rem] sm:min-h-0">
                                    <span
                                        className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-stone-400 text-sm sm:text-base pointer-events-none select-none"
                                        aria-hidden
                                    >
                                        🔎
                                    </span>
                                    <input
                                        type="text"
                                        enterKeyHint="search"
                                        autoComplete="off"
                                        placeholder="Search by name, cuisine, or area"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full min-h-[2.5rem] sm:min-h-0 pl-9 sm:pl-12 pr-11 sm:pr-28 py-2 sm:py-3.5 bg-transparent text-stone-900 placeholder-stone-400 focus:outline-none text-base"
                                    />

                                    {(searchQuery || selectedCity !== 'all') && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setSearchQuery('');
                                                setSelectedCity('all');
                                            }}
                                            className="absolute right-1.5 top-1/2 -translate-y-1/2 inline-flex items-center justify-center gap-1 min-w-9 min-h-9 sm:min-w-0 sm:min-h-0 sm:right-2 sm:px-2.5 sm:py-1.5 rounded-lg sm:rounded-xl bg-stone-100 hover:bg-stone-200 active:bg-stone-300 text-stone-700 text-sm font-semibold transition-colors"
                                            aria-label="Clear city and search"
                                        >
                                            <span className="text-sm leading-none sm:text-xs" aria-hidden>
                                                ✕
                                            </span>
                                            <span className="hidden sm:inline">Clear</span>
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Hint row — compact on mobile */}
                            <div className="mt-2 sm:mt-3 flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center justify-center gap-1.5 sm:gap-2 text-xs sm:text-xs md:text-sm text-stone-500">
                                <span className="inline-flex items-start gap-2 px-2.5 py-2 sm:px-3 sm:py-1 sm:items-center rounded-xl sm:rounded-full bg-white/85 border border-stone-200/90 text-left sm:text-center leading-snug shadow-sm sm:shadow-none">
                                    <span className="text-sm sm:text-base flex-shrink-0 leading-none mt-px sm:mt-0" aria-hidden>
                                        ✨
                                    </span>
                                    <span className="text-[13px] sm:text-inherit">
                                        <span className="font-medium text-stone-600 sm:font-normal">Tip:</span>{' '}
                                        try “indiranagar”, “italian”, or “dosa”
                                    </span>
                                </span>
                                <span className="inline-flex items-center justify-center gap-1.5 px-2.5 py-2 sm:px-3 sm:py-1 rounded-xl sm:rounded-full bg-amber-50 border border-amber-200 text-amber-900 font-semibold text-[13px] sm:text-sm">
                                    {totalResults} result{totalResults !== 1 ? 's' : ''}
                                </span>
                            </div>
                        </div>
                    </motion.div>
                </section>

                {/* Results */}
                {loading ? (
                    <motion.div
                        className="flex flex-col items-center justify-center py-12 sm:py-20 md:py-24"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                    >
                        <div className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 rounded-full border-2 border-amber-200 border-t-amber-500 animate-spin" />
                        <p className="text-stone-500 mt-3 sm:mt-5 font-medium text-sm sm:text-base">Loading restaurants...</p>
                    </motion.div>
                ) : filteredRestaurants.length === 0 ? (
                    <motion.div
                        className="text-center py-12 sm:py-20 md:py-24"
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        <div className="w-14 h-14 sm:w-16 sm:h-16 md:w-20 md:h-20 rounded-xl sm:rounded-2xl bg-white border border-stone-200 shadow-md flex items-center justify-center mx-auto mb-4 sm:mb-6 text-2xl sm:text-3xl md:text-4xl">
                            🍽️
                        </div>
                        <p className="text-stone-600 text-sm sm:text-base md:text-lg font-medium px-1">
                            {searchQuery.trim() ? 'No restaurants match your search.' : 'No restaurants listed yet.'}
                        </p>
                        {searchQuery.trim() && (
                            <button
                                type="button"
                                onClick={() => setSearchQuery('')}
                                className="mt-4 text-amber-600 hover:text-amber-700 font-semibold"
                            >
                                Clear search
                            </button>
                        )}
                    </motion.div>
                ) : (
                    <>
                        <motion.p
                            className="text-stone-500 text-xs sm:text-sm mb-4 sm:mb-6"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.2 }}
                        >
                            Showing {showingFrom}–{showingTo} of {totalResults} restaurant{totalResults !== 1 ? 's' : ''}
                        </motion.p>

                        {PaginationControls && <div className="mb-4 sm:mb-6">{PaginationControls}</div>}

                        <motion.section
                            className="grid grid-cols-2 lg:grid-cols-3 gap-1.5 sm:gap-4 md:gap-6"
                            variants={container}
                            initial="hidden"
                            animate="show"
                        >
                            {pagedRestaurants.map((r, index) => (
                                <motion.div key={r._id} variants={item}>
                                    <Link to={`/menu/${r._id}`} className="block group h-full">
                                        <div className="h-full rounded sm:rounded-xl md:rounded-2xl overflow-hidden bg-white border border-stone-200 hover:border-amber-200 hover:shadow-md sm:hover:shadow-xl hover:shadow-amber-100/50 transition-all duration-300 flex flex-col shadow-sm sm:shadow-md">
                                            {/* Image — shorter on mobile than square (2-col grid) */}
                                            <div className="relative aspect-[5/3] sm:aspect-[16/10] overflow-hidden">
                                                {r.logo || r.image || r.coverImage ? (
                                                    <>
                                                        <div className="absolute inset-0 bg-gradient-to-br from-amber-100 to-amber-50" />
                                                        {(() => {
                                                            // Prefer logos on listing cards (avoid scenery).
                                                            const src = r.logo || r.image || r.coverImage;
                                                            const isSvgLogo =
                                                                typeof src === 'string' &&
                                                                (src.startsWith('data:image/svg+xml') || src.endsWith('.svg'));
                                                            return (
                                                                <img
                                                                    src={src}
                                                                    alt={r.name || 'Restaurant'}
                                                                    loading="lazy"
                                                                    referrerPolicy="no-referrer"
                                                                    className={[
                                                                        'relative w-full h-full transition-transform duration-500 group-hover:scale-105 sm:group-hover:scale-110',
                                                                        isSvgLogo ? 'object-contain p-2 sm:p-10' : 'object-cover',
                                                                    ].join(' ')}
                                                                    onError={(e) => {
                                                                        e.currentTarget.onerror = null;
                                                                        e.currentTarget.src = restaurantFallbackLogo(r, index);
                                                                    }}
                                                                />
                                                            );
                                                        })()}
                                                    </>
                                                ) : (
                                                    <div className="w-full h-full bg-gradient-to-br from-amber-100 to-amber-50 flex items-center justify-center">
                                                        <span className="text-base sm:text-4xl md:text-5xl opacity-60">🍽️</span>
                                                    </div>
                                                )}
                                                <div className="absolute inset-0 bg-gradient-to-t from-stone-900/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                                {r.cuisine && (
                                                    <span className="absolute bottom-1 left-1 sm:bottom-3 sm:left-3 max-w-[calc(100%-0.5rem)] sm:max-w-none truncate px-1 py-0.5 sm:px-2.5 sm:py-1 rounded sm:rounded-lg bg-white/95 backdrop-blur-sm text-[9px] sm:text-xs font-medium text-stone-700 border border-stone-200 shadow-sm">
                                                        {r.cuisine}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="p-1.5 sm:p-4 md:p-5 flex flex-col flex-1 min-w-0">
                                                <h3 className="text-[10px] leading-tight sm:text-lg md:text-xl font-bold text-stone-900 group-hover:text-amber-800 transition-colors line-clamp-2 sm:line-clamp-1">
                                                    {r.name || 'Restaurant'}
                                                </h3>
                                                {(r.city || r.state) && (
                                                    <p className="text-stone-500 text-[8px] sm:text-sm mt-0.5 sm:mt-1.5 flex items-center gap-0.5 min-w-0">
                                                        <span className="shrink-0 text-[7px] sm:text-sm" aria-hidden>📍</span>
                                                        <span className="truncate leading-tight">{[r.city, r.state].filter(Boolean).join(', ')}</span>
                                                    </p>
                                                )}
                                                <div className="mt-auto pt-1 sm:pt-3 md:pt-4 border-t border-stone-100 flex flex-col gap-0 sm:gap-0.5 sm:flex-row sm:items-center sm:justify-between sm:gap-2">
                                                    <span className="text-stone-500 text-[8px] sm:text-sm flex items-center gap-0.5 min-w-0">
                                                        <span className="shrink-0" aria-hidden>🕐</span>
                                                        <span className="truncate leading-tight">{formatTime(r.openingTime)} – {formatTime(r.closingTime)}</span>
                                                    </span>
                                                    <span className="text-amber-600 text-[8px] sm:text-sm font-semibold group-hover:text-amber-700 transition-colors shrink-0">
                                                        <span className="sm:hidden">Menu →</span>
                                                        <span className="hidden sm:inline">View menu →</span>
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </Link>
                                </motion.div>
                            ))}
                        </motion.section>

                        {PaginationControls && <div className="mt-5 sm:mt-8">{PaginationControls}</div>}
                    </>
                )}
            </main>

            {/* Footer */}
            <footer className="relative z-10 border-t border-stone-200 bg-white/50 mt-6 sm:mt-8">
                <div className="max-w-6xl mx-auto px-3 sm:px-6 py-4 sm:py-6">
                    <Link
                        to="/"
                        className="inline-flex items-center gap-1.5 sm:gap-2 text-stone-500 hover:text-stone-900 text-xs sm:text-sm font-medium transition-colors"
                    >
                        <span>←</span> Back to Home
                    </Link>
                </div>
            </footer>
        </div>
    );
};

export default RestaurantsPage;
